import React from "react";
import { Box, Button, Chip, ListItem, TextField, Tooltip, Typography } from "@mui/material";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { formatCurrency } from "../../utils/namesFormater";

export interface UnchangedItemData {
  policyNumber: string;
  simItemId: number | null;
  contactId: number;
  productTypeLabel: string | null;
  handlerChange: string;
  savingFeeChange: string;
  fundsSum: number | null;
  savedNote: string;
  integrityWarning?: string;
}

interface Props {
  item: UnchangedItemData;
  noteValue: string;
  editingNoteKey: string | null;
  notesOnlyMode: boolean;
  listItemBgColor?: string;
  contactColorMap: Record<number, string>;
  onNoteClick: (policyNumber: string, savedNote: string) => void;
  onNoteChange: (policyNumber: string, value: string) => void;
  onNoteBlur: (policyNumber: string) => void;
  onNoteUpdate?: (policyNumber: string, note: string) => void;
  onNoteCancel: (policyNumber: string, savedNote: string) => void;
}

const UnchangedListItem: React.FC<Props> = ({
  item,
  noteValue,
  editingNoteKey,
  notesOnlyMode,
  listItemBgColor,
  contactColorMap,
  onNoteClick,
  onNoteChange,
  onNoteBlur,
  onNoteUpdate,
  onNoteCancel,
}) => {
  return (
    <ListItem
      sx={{
        borderRadius: 1,
        mb: 0.5,
        backgroundColor: listItemBgColor ?? "#ffffff",
        border: "1px solid #e0e0e0",
        ...(item.contactId && contactColorMap[item.contactId] ? { borderRight: `6px solid ${contactColorMap[item.contactId]}` } : {}),
        display: "grid",
        gridTemplateColumns: "5fr 5fr 2fr",
        alignItems: "start",
        gap: 1,
        py: 1,
        px: 2,
      }}
    >
      {/* Col 1 */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
          <Chip
            label="ללא שינוי"
            size="small"
            sx={{ backgroundColor: "#9e9e9e", color: "#fff", fontWeight: 600, fontSize: "0.8rem", height: 24 }}
          />
          {item.productTypeLabel && (
            <Typography variant="body2" color="text.secondary">{item.productTypeLabel}</Typography>
          )}
          <Typography variant="body2" color="text.secondary">{item.policyNumber}</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          {item.handlerChange && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">יצרן:</Typography>
              <Typography variant="body2">{item.handlerChange}</Typography>
            </Box>
          )}
          {item.savingFeeChange && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">דמ״נ צבירה:</Typography>
              <Typography variant="body2" fontWeight={600}>{item.savingFeeChange}</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Col 2: note */}
      <Box sx={{ cursor: "pointer" }} onClick={() => onNoteClick(item.policyNumber, item.savedNote)}>
        {editingNoteKey === item.policyNumber ? (
          <Box>
            <TextField
              autoFocus
              multiline
              rows={3}
              size="small"
              fullWidth
              value={noteValue}
              onChange={(e) => onNoteChange(item.policyNumber, e.target.value)}
              onBlur={() => onNoteBlur(item.policyNumber)}
              placeholder="הערה..."
              inputProps={{ dir: "rtl", style: { direction: "rtl", textAlign: "right", unicodeBidi: "plaintext" } }}
              onFocus={(e) => { const len = e.target.value.length; e.target.setSelectionRange(len, len); }}
              onClick={(e) => e.stopPropagation()}
            />
            {notesOnlyMode && (
              <Box sx={{ display: "flex", gap: 1, mt: 0.5 }} onClick={(e) => e.stopPropagation()}>
                <Button size="small" variant="contained" onClick={() => onNoteUpdate?.(item.policyNumber, noteValue)}>עדכן</Button>
                <Button size="small" variant="outlined" onClick={() => onNoteCancel(item.policyNumber, item.savedNote)}>בטל</Button>
              </Box>
            )}
          </Box>
        ) : noteValue ? (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
            <NoteAltOutlinedIcon fontSize="small" sx={{ color: "primary.main", mt: 0.2, flexShrink: 0 }} />
            <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
              {noteValue}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <NoteAltOutlinedIcon fontSize="small" sx={{ color: "action.disabled" }} />
            <Typography variant="caption" color="text.disabled">הוסף הערה</Typography>
          </Box>
        )}
      </Box>

      {/* Col 3: funds sum */}
      {item.fundsSum != null && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25 }}>
          <Typography variant="h6" fontWeight="bold" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {formatCurrency(item.fundsSum)}
          </Typography>
          {item.integrityWarning && (
            <Tooltip arrow title={<span style={{ whiteSpace: "pre-line", direction: "rtl", display: "block", textAlign: "right" }}>{item.integrityWarning}</span>}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, cursor: "default" }}>
                <WarningAmberIcon sx={{ fontSize: "0.9rem", color: "error.main" }} />
                <Typography variant="caption" sx={{ color: "error.main", fontWeight: 700 }}>* אי-התאמה בנתוני מסלולים</Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
      )}
    </ListItem>
  );
};

export default UnchangedListItem;
